import React, { useMemo, useState } from 'react';
import {
  DEFAULT_SCALE_INPUTS,
  HOW_STEPS,
  NICHE_PROFILES,
  calculateScaleScenarios,
  formatCount,
  formatMoney,
  formatPercent,
  getNicheCplGrowthPercent,
  getScaleInsight,
} from './scaleCalculatorLogic';
import RevenueLeversNumberField from '../RevenueLevers/RevenueLeversNumberField';
import '../RevenueLevers/RevenueLevers.css';
import './ScaleCalculator.css';

const ScaleCalculator = () => {
  const locale = 'ru-RU';
  const [inputs, setInputs] = useState(DEFAULT_SCALE_INPUTS);

  const setField = (key) => (value) => setInputs((prev) => ({ ...prev, [key]: value }));

  const result = useMemo(() => calculateScaleScenarios(inputs), [inputs]);
  const insight = useMemo(() => getScaleInsight(result.scenarios), [result.scenarios]);
  const nicheProfile = NICHE_PROFILES[inputs.niche] || NICHE_PROFILES.local_service;
  const x3Scenario = result.scenarios.find((s) => s.mult === 3);
  const nicheCplGrowth = getNicheCplGrowthPercent(inputs.niche, 3);

  return (
    <div className="sc-tool">
      <div className="rl-legend">
        <div className="rl-legend-item">
          <span className="rl-legend-dot rl-legend-dot--input" />
          {'Вводите вы'}
        </div>
        <div className="rl-legend-item">
          <span className="rl-legend-dot rl-legend-dot--calc" />
          {'Считается автоматически'}
        </div>
      </div>

      <div className="sc-layout">
        <div className="rl-panel">
          <div className="rl-panel-title">{'Текущие показатели'}</div>
          <RevenueLeversNumberField
            label={'Рекламный бюджет в месяц'}
            unit={'₸'}
            value={inputs.budget}
            onChange={setField('budget')}
            min={1}
          />
          <RevenueLeversNumberField
            label={'Лидов в месяц'}
            unit={'шт'}
            value={inputs.leads}
            onChange={setField('leads')}
            min={1}
          />
          <RevenueLeversNumberField
            label={'Конверсия лид → продажа'}
            unit={'%'}
            value={inputs.conv}
            onChange={setField('conv')}
            min={0.1}
            max={100}
            step={0.5}
          />
          <RevenueLeversNumberField
            label={'Средний чек'}
            unit={'₸'}
            value={inputs.check}
            onChange={setField('check')}
            min={1}
          />
          <div className="rl-field">
            <div className="rl-field-label">
              <span>{'Тип ниши'}</span>
            </div>
            <div className="rl-input-wrap rl-input-wrap--select">
              <select
                className="rl-input rl-select"
                value={inputs.niche}
                onChange={(e) => setField('niche')(e.target.value)}
              >
                {Object.entries(NICHE_PROFILES).map(([id, profile]) => (
                  <option key={id} value={id}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="rl-field-note">{nicheProfile.desc}</div>
            <div className="sc-niche-examples">{nicheProfile.examples}</div>
            <div className="sc-niche-scale" key={inputs.niche}>
              {`Для этой ниши при масштабе ×3 CPL типично вырастет на +${nicheCplGrowth}% (сейчас ${Math.round(result.cplBase).toLocaleString(locale)} ₸ → ~${x3Scenario ? Math.round(x3Scenario.cpl).toLocaleString(locale) : '—'} ₸).`}
            </div>
          </div>
        </div>

        <div className="rl-rev-current">
          <div>
            <div className="rl-rev-label">{'CPL сейчас'}</div>
            <div className="rl-rev-value">{formatMoney(result.cplBase, locale)}</div>
          </div>
          <div className="rl-rev-formula">{'CPL = бюджет ÷ лиды'}</div>
        </div>
      </div>

      <div className="sc-insight">{insight.text}</div>

      <div className="rl-section-title">{'Сценарии масштабирования'}</div>
      <div className="rl-table-wrap">
        <table className="rl-table">
          <thead>
            <tr>
              <th>{'Масштаб'}</th>
              <th>{'Бюджет'}</th>
              <th>{'CPL'}</th>
              <th>{'Лиды'}</th>
              <th>{'Прирост лидов'}</th>
              <th>{'Выручка'}</th>
              <th>{'ROMI'}</th>
            </tr>
          </thead>
          <tbody>
            {result.scenarios.map((row) => (
              <tr key={row.mult} className={row.mult === 1 ? 'sc-row--current' : undefined}>
                <td>{row.mult === 1 ? 'Сейчас' : `×${row.mult}`}</td>
                <td>{formatMoney(row.budget, locale)}</td>
                <td>{formatMoney(row.cpl, locale)}</td>
                <td>{formatCount(row.leads, locale)}</td>
                <td className="sc-growth">{row.mult === 1 ? '—' : formatPercent(row.leadsGrowth)}</td>
                <td>{formatMoney(row.revenue, locale)}</td>
                <td className={row.romi >= 0 ? 'sc-romi--pos' : 'sc-romi--neg'}>
                  {Math.round(row.romi)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rl-how">
        <div className="rl-panel-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="rl-how-step">
            <div className="rl-how-num">{step.num}</div>
            <div className="rl-how-text">
              <strong>{step.title}</strong> {step.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScaleCalculator;
